import React, { useState } from "react";
import {
  Option,
  BaselineDefaultsRegulationsFormPropertyType,
  BaselineDefaultsRegulationsFormDefaults,
  BaselineDefaultsRegulationsFormCountyType,
  DefaultsAdminGrowthRatesFormPropertyType,
  DefaultsPageConfiguration,
  BaselineDefaultsRegulationsFormRegulationType
} from "api";
import createDataTable, {
  IPagedTableState,
  ColumnDefinition,
  DataTableProps
} from "components/controls/createTable";

import Dropdown from "components/controls/Dropdown";
import listUtils from "utils/listUtils";
import { Calendar } from "primereact/calendar";
import { format } from "date-fns";
import ValidationWrapper from "components/controls/ValidationWrapper";
import useStyle from "./useStyle";
import Input from "components/controls/Input";
import OverlayPanel from "components/controls/OverlayPanel";

export interface DefaultsRegulationsFormTableProps {
  defaults?: BaselineDefaultsRegulationsFormDefaults[] | undefined;
  regulations: BaselineDefaultsRegulationsFormRegulationType[];
  counties: BaselineDefaultsRegulationsFormCountyType[];
  properties: DefaultsAdminGrowthRatesFormPropertyType[];
  onChange: (defaults: BaselineDefaultsRegulationsFormDefaults[]) => void;
  hidePropertyColumn?: boolean | undefined;
  hideCountyTypeColumn?: boolean | undefined;
  hideRemoveRowColumn?: boolean | undefined;
  paging?: boolean | undefined;
  onAddRowAction?: (() => void) | undefined;
  includeRemoveRowColumn?: "show" | "buffer" | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

interface TableState extends IPagedTableState {
  divisionFilter?: Option;
  sortField?: any;
  sortOrder?: any;
  sortState?: "unsorted" | "asc" | "desc" | undefined;
}

const DefaultsRegulationsFormTable: React.FC<DefaultsRegulationsFormTableProps> =
  function (props: DefaultsRegulationsFormTableProps) {
    const ab1919RegulationKey = "c99ccf6e-70b5-4c47-a81c-fb1246667cd3";
    const tenancyDurationRegulationKey = "9d4d5913-9485-4571-98ad-46bf9c1db77e";
    const [table, setTable] = useState<TableState>({
      offset: 0,
      limit: 10,
      sortOrder: 1
    });

    const classes = useStyle();

    const startDateName = "Start Date";
    const daysName = "Days";
    const maxPercentageName = "Max %";

    const onRowChanged = (
      oldRow: BaselineDefaultsRegulationsFormDefaults,
      newRow: BaselineDefaultsRegulationsFormDefaults
    ) => {
      if (!props.defaults) {
        return;
      }

      if (oldRow.regulationType !== newRow.regulationType) {
        newRow.endDate = undefined;
        newRow.startDate = undefined;
        newRow.numberOfDays = undefined;
        newRow.percentageCap = undefined;
      }

      props.onChange(
        listUtils.copyListWithNewItem(props.defaults, oldRow, newRow)
      );
    };

    const onRemoveRow = (row: BaselineDefaultsRegulationsFormDefaults) => {
      if (!props.defaults) {
        return;
      }

      const newDefaults = listUtils.copyListWithItemRemoved(
        props.defaults,
        row
      );
      props.onChange(newDefaults);
    };

    const onDefaultsRegulationsFormPropertyChange = (
      defaultsRow: BaselineDefaultsRegulationsFormDefaults
    ) => {
      return (
        selectedProperty:
          | BaselineDefaultsRegulationsFormPropertyType
          | undefined
      ) => {
        const newDefaultsRow: BaselineDefaultsRegulationsFormDefaults = {
          ...defaultsRow,
          selectedPropertyType: selectedProperty
        };

        onRowChanged(defaultsRow, newDefaultsRow);
      };
    };

    const onDefaultsRegulationsFormCountyChange = (
      defaultsRow: BaselineDefaultsRegulationsFormDefaults
    ) => {
      return (
        selectedCountyType:
          | BaselineDefaultsRegulationsFormCountyType
          | undefined
      ) => {
        const newDefaultsRow: BaselineDefaultsRegulationsFormDefaults = {
          ...defaultsRow,
          selectedCountyType: selectedCountyType
        };

        onRowChanged(defaultsRow, newDefaultsRow);
      };
    };

    const getNumberOfDaysBody = function (rowData: any, column: any) {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;
      if (
        !defaults.regulationType ||
        defaults.regulationType.key !== tenancyDurationRegulationKey
      ) {
        return;
      }

      let dropDown = (
        <Input
          onChange={(value) =>
            onRowChanged(defaults, {
              ...defaults,
              numberOfDays: value == null ? undefined : Number.parseInt(value)
            })
          }
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["NumberOfDays"]
          }
          value={
            (defaults.numberOfDays && defaults.numberOfDays.toString()) ||
            undefined
          }
        />
      );

      let label = (
        <div>
          {defaults.regulationType && defaults.regulationType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const helpOverlay = (
      <OverlayPanel
        buttonTitle={""}
        buttonIcon={"pi pi-question-circle"}
        buttonStyle={{ fontSize: "1.5em" }}
      >
        <span>
          <b>AB1919</b> - Using rent on [{startDateName}] as anchor rent, the
          cumulative <br /> rent increase for a tenant cannot exceed [
          {maxPercentageName}] percent.
          <br />
          <br />
          <b>Tenancy Duration</b> - For new tenants, rent cannot be increased
          until <br /> [{daysName}] days after move in date.
        </span>
      </OverlayPanel>
    );

    const getRegulationTypeDefaultsBody = function (rowData: any, column: any) {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;
      let selectedValue =
        defaults.regulationType &&
        props.regulations.find((x) => x.key === defaults.regulationType!.key);

      let dropDown = (
        <>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ width: "100%" }}>
              <Dropdown
                onChange={(value) =>
                  onRowChanged(defaults, { ...defaults, regulationType: value })
                }
                validationMessage={
                  defaults.fieldValidationErrors &&
                  defaults.fieldValidationErrors["RegulationType"]
                }
                selectedValue={selectedValue}
                options={props.regulations}
                style={{ minWidth: "100%", maxWidth: "100%" }}
              />
            </div>
            {helpOverlay}
          </div>
        </>
      );

      let label = (
        <div>
          {defaults.regulationType && defaults.regulationType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getStartDateBody = function (rowData: any, column: any) {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;
      if (
        !defaults.regulationType ||
        defaults.regulationType.key !== ab1919RegulationKey
      ) {
        return;
      }

      let dropDown = (
        <ValidationWrapper
          showErrorState={
            defaults.fieldValidationErrors != null &&
            defaults.fieldValidationErrors["StartDate"] != null
          }
        >
          <Calendar
            value={defaults.startDate && new Date(defaults.startDate)}
            tooltip={
              defaults.fieldValidationErrors &&
              defaults.fieldValidationErrors["StartDate"]
            }
            readOnlyInput={true}
            onChange={(e) => {
              onRowChanged(defaults, {
                ...defaults,
                startDate: (e.value as Date) || undefined
              });
            }}
            appendTo={document.getElementById("root")}
          ></Calendar>
        </ValidationWrapper>
      );

      let label = (
        <div>
          {defaults.startDate &&
            format(new Date(defaults.startDate), "MM/dd/yyyy")}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getEndDateBody = function (rowData: any, column: any) {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;
      if (
        !defaults.regulationType ||
        defaults.regulationType.key !== ab1919RegulationKey
      ) {
        return;
      }

      let dropDown = (
        <ValidationWrapper
          showErrorState={
            defaults.fieldValidationErrors != null &&
            defaults.fieldValidationErrors["EndDate"] != null
          }
        >
          <Calendar
            value={defaults.endDate && new Date(defaults.endDate)}
            tooltip={
              defaults.fieldValidationErrors &&
              defaults.fieldValidationErrors["EndDate"]
            }
            clearButtonStyleClass={classes.whiteFont}
            todayButtonClassName={classes.disabled}
            showButtonBar={true}
            readOnlyInput={true}
            onChange={(e) =>
              onRowChanged(defaults, {
                ...defaults,
                endDate: (e.value as Date) || undefined
              })
            }
            appendTo={document.getElementById("root")}
          ></Calendar>
        </ValidationWrapper>
      );

      let label = (
        <div>
          {defaults.endDate && format(new Date(defaults.endDate), "MM/dd/yyyy")}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getPropertyDefaultsBody = function (rowData: any, column: any) {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;

      let dropDown = (
        <Dropdown
          onChange={onDefaultsRegulationsFormPropertyChange(defaults)}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedPropertyType"]
          }
          selectedValue={defaults.selectedPropertyType}
          options={props.properties}
          style={{ minWidth: "100%", maxWidth: "100%" }}
        />
      );

      let label = (
        <div>
          {defaults.selectedPropertyType &&
            defaults.selectedPropertyType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getCountyDefaultsBody = function (rowData: any, column: any) {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;

      let dropDown = (
        <Dropdown
          onChange={onDefaultsRegulationsFormCountyChange(defaults)}
          selectedValue={defaults.selectedCountyType}
          options={props.counties}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["SelectedCountyType"]
          }
        />
      );

      let label = (
        <div>
          {defaults.selectedCountyType &&
            defaults.selectedCountyType.displayValue}
        </div>
      );

      if (props.pageConfiguration && props.pageConfiguration.isEditEnabled) {
        return dropDown;
      } else {
        return label;
      }
    };

    const getBody = (rowData: any, column: any) => {
      const defaults = rowData as BaselineDefaultsRegulationsFormDefaults;

      if (
        !defaults.regulationType ||
        defaults.regulationType.key !== ab1919RegulationKey
      ) {
        return;
      }

      return (
        <Input
          value={defaults.percentageCap}
          validationMessage={
            defaults.fieldValidationErrors &&
            defaults.fieldValidationErrors["PercentageCap"]
          }
          onChange={(value) =>
            onRowChanged(defaults, { ...defaults, percentageCap: value })
          }
        />
      );
    };

    const getColumns = function (): any[] {
      const columns = [] as ColumnDefinition<
        BaselineDefaultsRegulationsFormDefaults,
        keyof BaselineDefaultsRegulationsFormDefaults
      >[];

      const countyDropdownColumnStyle = { width: "150px" };
      const propertyDropdownColumnStyle = { width: "150px" };

      if (!props.hidePropertyColumn) {
        columns.push({
          header: "Property",
          style: propertyDropdownColumnStyle,
          body: getPropertyDefaultsBody
        });
      } else {
        columns.push({ header: "", style: propertyDropdownColumnStyle });
      }

      if (!props.hideCountyTypeColumn) {
        columns.push({
          header: "County",
          style: countyDropdownColumnStyle,
          body: getCountyDefaultsBody
        });
      } else {
        columns.push({ header: "", style: countyDropdownColumnStyle });
      }

      return columns.concat([
        {
          field: "regulationType",
          header: "Regulation Type",
          style: { width: "150px" },
          headerStyle: { width: "180px", textAlign: "center" },
          body: getRegulationTypeDefaultsBody
        },
        {
          field: "startDate",
          header: startDateName,
          style: { width: "100px", textAlign: "center" },
          body: getStartDateBody
        },
        {
          field: "endDate",
          header: "End Date",
          style: { width: "100px", textAlign: "center" },
          body: getEndDateBody
        },
        {
          field: "numberOfDays",
          header: daysName,
          style: { width: "70px", textAlign: "center" },
          body: getNumberOfDaysBody
        },
        {
          field: "percentageCap",
          header: maxPercentageName,
          style: { width: "70px", textAlign: "center" },
          body: (row: any, column: any) => getBody(row, column)
        }
      ]);
    };

    const tableProps: DataTableProps<
      BaselineDefaultsRegulationsFormDefaults,
      TableState
    > = {
      value: props.defaults || undefined,
      editMode: "cell",
      state: table,
      updateState: setTable,
      paging: props.paging,
      // TODO: This functionality only works when added at this leve. I was unable to get it to work when I moved it up to the createDataTable function
      sortField: table.sortField,
      sortOrder: table.sortOrder,
      onSort: (e: any) => {
        //clear sort column
        if (
          table.sortOrder === -1 &&
          e.sortOrder === 1 &&
          table.sortField === e.sortField
        ) {
          setTable({
            ...table,
            sortField: undefined,
            sortOrder: undefined
          });
        } else {
          setTable({
            ...table,
            sortField: e.sortField,
            sortOrder: e.sortOrder
          });
        }
      },
      onAddRowAction: props.onAddRowAction,
      onRemoveRowAction:
        props.includeRemoveRowColumn === "show" ? onRemoveRow : undefined,
      hasRemoveRowBuffer: props.includeRemoveRowColumn === "buffer",
      emptyMessage: "No defaults have been added."
    };

    const dataTable = createDataTable<
      BaselineDefaultsRegulationsFormDefaults,
      TableState
    >(getColumns(), tableProps);

    return <div>{dataTable}</div>;
  };

export default DefaultsRegulationsFormTable;
